/**
 * @sazeo/sdk — Invites (tenant member invite flow).
 *
 * Wraps the existing `/invitations` Edge Function and the new bulk + resend
 * extensions added in Phase 7 Wave 2. All calls run with the caller's JWT;
 * service_role paths live in the `invitations-admin` internal function.
 *
 * This module is additive — it deliberately reuses the schemas in
 * @sazeo/schemas/invites.ts rather than redefining them here.
 */

import {
  type InvitationAcceptInput,
  InvitationAcceptInputSchema,
  type InvitationAcceptResponse,
  InvitationAcceptResponseSchema,
  type InvitationListResponse,
  InvitationListResponseSchema,
  type InvitationPublic,
  type InviteBulkCreateInput,
  InviteBulkCreateInputSchema,
  type InviteBulkCreateResponse,
  InviteBulkCreateResponseSchema,
  type InviteResendInput,
  InviteResendInputSchema,
  type InviteResendResponse,
  InviteResendResponseSchema,
  type InviteWithTeamsCreateInput,
  InviteWithTeamsCreateInputSchema,
} from '@sazeo/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClientLike } from './people';

export class InvitesApiError extends Error {
  readonly code: string;
  readonly status: number;
  constructor(code: string, message: string, status: number) {
    super(message);
    this.name = 'InvitesApiError';
    this.code = code;
    this.status = status;
  }
}

type ErrorEnvelope = { error?: { code?: string; message?: string } };

export const invitesKeys = {
  all: ['tenant-invites'] as const,
  list: (tenantId?: string) => [...invitesKeys.all, 'list', tenantId ?? 'active'] as const,
} as const;

async function getAuthHeaders(supabase: SupabaseClientLike): Promise<Record<string, string>> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { authorization: `Bearer ${token}` } : {};
}

async function readError(res: Response): Promise<InvitesApiError> {
  const body = (await res.json().catch(() => null)) as ErrorEnvelope | null;
  return new InvitesApiError(
    body?.error?.code ?? 'invite_failed',
    body?.error?.message ?? res.statusText,
    res.status,
  );
}

async function apiFetch<T>(
  supabase: SupabaseClientLike,
  path: string,
  parse: (body: unknown) => T,
  init?: RequestInit,
): Promise<T> {
  const headers = await getAuthHeaders(supabase);
  const res = await fetch(`/functions/v1/${path}`, {
    ...init,
    headers: { 'content-type': 'application/json', ...headers, ...init?.headers },
  });
  if (!res.ok) throw await readError(res);
  return parse(await res.json());
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

export function listTenantInvites(supabase: SupabaseClientLike): Promise<InvitationListResponse> {
  return apiFetch(supabase, 'invitations', (body) => InvitationListResponseSchema.parse(body));
}

export function createTenantInvite(
  supabase: SupabaseClientLike,
  input: InviteWithTeamsCreateInput,
): Promise<InvitationPublic> {
  const payload = InviteWithTeamsCreateInputSchema.parse(input);
  return apiFetch(
    supabase,
    'invitations',
    (body) => {
      const parsed = body as { invitation?: unknown };
      if (!parsed.invitation) throw new Error('missing invitation in response');
      return parsed.invitation as InvitationPublic;
    },
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export function bulkCreateTenantInvites(
  supabase: SupabaseClientLike,
  input: InviteBulkCreateInput,
): Promise<InviteBulkCreateResponse> {
  const payload = InviteBulkCreateInputSchema.parse(input);
  return apiFetch(
    supabase,
    'invitations/bulk',
    (body) => InviteBulkCreateResponseSchema.parse(body),
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

export function revokeTenantInvite(
  supabase: SupabaseClientLike,
  id: string,
): Promise<InvitationPublic> {
  return apiFetch(
    supabase,
    `invitations/${id}/revoke`,
    (body) => (body as { invitation: InvitationPublic }).invitation,
    { method: 'POST' },
  );
}

export function resendTenantInvite(
  supabase: SupabaseClientLike,
  input: InviteResendInput,
): Promise<InviteResendResponse> {
  const payload = InviteResendInputSchema.parse(input);
  return apiFetch(
    supabase,
    `invitations/${payload.id}/resend`,
    (body) => InviteResendResponseSchema.parse(body),
    { method: 'POST' },
  );
}

export function acceptTenantInvite(
  supabase: SupabaseClientLike,
  input: InvitationAcceptInput,
): Promise<InvitationAcceptResponse> {
  const payload = InvitationAcceptInputSchema.parse(input);
  return apiFetch(
    supabase,
    'invitations/accept',
    (body) => InvitationAcceptResponseSchema.parse(body),
    {
      method: 'POST',
      body: JSON.stringify(payload),
    },
  );
}

// ---------------------------------------------------------------------------
// React Query hooks
// ---------------------------------------------------------------------------

export function useTenantInvites(supabase: SupabaseClientLike, tenantId?: string, enabled = true) {
  return useQuery({
    queryKey: invitesKeys.list(tenantId),
    queryFn: () => listTenantInvites(supabase),
    enabled,
  });
}

export function useCreateInvite(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteWithTeamsCreateInput) => createTenantInvite(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitesKeys.all });
    },
  });
}

export function useBulkCreateInvites(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteBulkCreateInput) => bulkCreateTenantInvites(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitesKeys.all });
    },
  });
}

export function useRevokeInvite(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => revokeTenantInvite(supabase, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitesKeys.all });
    },
  });
}

export function useResendInvite(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InviteResendInput) => resendTenantInvite(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitesKeys.all });
    },
  });
}

export function useAcceptInvite(supabase: SupabaseClientLike) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvitationAcceptInput) => acceptTenantInvite(supabase, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invitesKeys.all });
    },
  });
}
